import React, { ComponentProps, useCallback, useContext, useMemo, useState } from 'react';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { useMutation, useQuery } from '@apollo/client';
import CustomerContact from '../../graphql/entities/customer/CustomerContact';
import CustomerContactsOnlyQuery from '../../graphql/queries/customer/CustomerContactsOnlyQuery.graphql';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import FluentModal from './FluentModal';
import { BasicHeader } from '../../actions/BasicHeader';
import CustomerGlobalContact from '../../graphql/entities/customer/CustomerGlobalContact';
import CustomerGlobalContactWorkspace from '../../views/customer/CustomerGlobalContactWorkspace';
import {
  ICustomerContactMutationResult,
  ICustomerContactMutationVariables,
  ICustomerContactsOnlyQueryResult,
  ICustomerContactsOnlyQueryVariables,
  ISalesOrder,
  ISalesOrderInput,
  ICustomerInput,
  ICustomerContactInput,
  ICustomerGlobalContact,
  ICustomerGlobalContactInput,
  AlertType,
} from '../../graphql/schema/p4types';
import { GlobeSearch20Regular, GlobeSearchRegular } from '@fluentui/react-icons';
import { IDropdownProps } from '../../@types/dropdown';
import { makeStyles } from '@fluentui/react-components';

export const CustomerGlobalContactLookup = ({
  modalOpen,
  hideModal,
  onRowClick,
  addToCustomer = true,
}: {
  modalOpen: boolean;
  hideModal: (hide: boolean) => void;
  onRowClick: (rowData: any) => void;
  addToCustomer: boolean;
}) => {
  const connectedForm = useContext(ConnectedFormContext);
  const handleQueryError = useComponentQueryErrorHandling();
  const [saveCustomerContact] = useMutation<ICustomerContactMutationResult, ICustomerContactMutationVariables>(
    CustomerContact.update!,
    {
      onError: handleQueryError,
    }
  );
  const customerId = (connectedForm.values as ISalesOrder).customer?.id;
  const { data: customerData } = useQuery<ICustomerContactsOnlyQueryResult, ICustomerContactsOnlyQueryVariables>(
    CustomerContactsOnlyQuery,
    {
      variables: {
        id: customerId,
      },
      onError: handleQueryError,
      skip: !customerId,
    }
  );
  const customer = useMemo(() => {
    return normalizeVariables<any, Maybe<ICustomerInput>>(customerData?.data);
  }, [customerData?.data]);
  const { dispatchSimpleToast } = useP4ToastController();

  return (
    <FluentModal isOpen={modalOpen} onDismiss={() => hideModal(true)}>
      <div style={{ position: 'relative', height: '80vh', width: '70vw' }}>
        <BasicHeader
          secondaryTitle={
            addToCustomer &&
            'The selected contact will be added to the Customer record then selected against this order, filling in the delivery details accordingly'
          }
        >
          Select Global Contact
        </BasicHeader>
        <CustomerGlobalContactWorkspace
          embeddedView={true}
          viewScreenName={`${CustomerGlobalContact.workspaceUrl}Modal`}
          onRowClick={(value: ICustomerGlobalContact) => {
            if (addToCustomer && !customer) {
              dispatchSimpleToast(
                'No Customer',
                'A customer must be selected on the order before delivery contact can be set.',
                AlertType.Warning
              );
              return;
            }

            //Shallow remove id/uuid/concurrencyVersion from the contact record to clone it.
            const { id, uuid, concurrencyVersion, attributeValues, ...clonedContact } =
              value as ICustomerGlobalContactInput;
            const customerContact = normalizeVariables({
              ...(clonedContact as ICustomerContactInput),
              //Also need to strip id/uuid/concurrencyVersion from the attributeValues.
              attributeValues:
                attributeValues?.map(({ id, uuid, concurrencyVersion, ...attributeValue }: any) => attributeValue) ??
                [],
              customerGlobalContact: value as ICustomerGlobalContactInput,
              customer: (connectedForm.values as ISalesOrderInput).customer,
            }) as ICustomerContactInput;

            if (addToCustomer) {
              saveCustomerContact({
                variables: {
                  input: {
                    customerContact: customerContact,
                  },
                },
              }).then((result) => {
                onRowClick(normalizeVariables(result?.data?.customerContactUpdate?.result));
              });
            } else {
              onRowClick(customerContact);
            }
          }}
          onClose={() => {
            hideModal(true);
          }}
          noAddOption={true}
        />
      </div>
    </FluentModal>
  );
};

const useStyles = makeStyles({
  globalLookupIcon: {
    display: 'flex',
    color: '#999',
  },
});

export const StandaloneGlobalContactLookupWrapper = ({
  focusOnInput,
}: ComponentProps<Required<IDropdownProps>['customIcon']>) => {
  const connectedForm = useContext(ConnectedFormContext);

  //Directly set the address and ignore the contact field.
  const selectValue = useCallback(
    (rowData) => {
      connectedForm.change('deliveryAddress', rowData.address);
      connectedForm.change('deliveryName', rowData.name.fullName);
    },
    [connectedForm]
  );

  return (
    <CustomerGlobalContactLookupIcon selectValue={selectValue} focusOnInput={focusOnInput} addToCustomer={false} />
  );
};

export const CustomerGlobalContactLookupIcon = ({
  selectValue,
  focusOnInput,
  addToCustomer, // ← Add this prop
}: ComponentProps<Required<IDropdownProps>['customIcon']> & {
  addToCustomer: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const styles = useStyles();

  return (
    <>
      <CustomerGlobalContactLookup
        modalOpen={isOpen}
        hideModal={(hide) => setIsOpen(!hide)}
        addToCustomer={addToCustomer}
        onRowClick={(rowData) => {
          selectValue(rowData);
          setIsOpen(false);
          focusOnInput();
        }}
      />
      <GlobeSearch20Regular onClick={() => setIsOpen(true)} className={styles.globalLookupIcon} />
    </>
  );
};
